import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Router } from '@angular/router';
import { CartService } from '../../../services/cart.service';
import { Product } from '../../../models/product.model';
import * as moment from 'moment';
import { CommonService } from 'src/app/services/common.service';
@Component({
  selector: 'app-multiple-line',
  templateUrl: './multiple-line.component.html',
  styleUrls: ['./multiple-line.component.less'],
})
export class MultipleLineComponent implements OnInit {
  @Input() isModal: boolean = false;
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();
  @Output() canAdd: EventEmitter<boolean> = new EventEmitter<boolean>();
  isVisible: boolean = false;
  form!: FormGroup;
  initialItemData: any;
  constructor(
    private fb: FormBuilder,
    private message: NzMessageService,
    private router: Router,
    private cartService: CartService,
    private commonService: CommonService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      itemsFormArray: this.fb.array([]),
    });
    // init data
    this.initialItemData = {
      materialNumber: '',
      date: null,
      quantity: 1,
    };
    this.addLines();
    // Add subscription to trigger the isMaterialNumberValid method when the state of FormArray changes
    //StatusChanges is suitable for obtaining the validation status of controls; 
    //Value Changes are used to monitor changes in the actual value of a control
    this.itemsFormArray.statusChanges.subscribe(() => {
      const canAdd = this.isMaterialNumberValid();
      this.canAdd.emit(canAdd);
    });
  }

  onFocusLastMaterialNumber(index: number) {
    if (index === this.itemsFormArray.length - 1) {
      this.addFormItem();
    }
  }
  chooseDate(product: any): void {
    const oneWeekLater = moment().add(7, 'days');
    product.get('date').setValue(oneWeekLater.format('YYYY-MM-DD'));
  }
  disabledDate = (current: Date): boolean => {
    if (this.isAutoPlusSevenNeed()) {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + 7);
      targetDate.setHours(0, 0, 0, 0);
      return current < targetDate; // disable the date before today
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return current < today; // disable the date before today
    }
  };

  isAutoPlusSevenNeed() {
    const storedCustomer = localStorage.getItem("customer");
    const customerInfo = storedCustomer ? JSON.parse(storedCustomer) : null;
    const salesOrg = this.getOrgCode();
    if (customerInfo !== null && salesOrg === 'CN20-G4-40') {
      return true;
    } else {
      return false;
    }
  }

  getOrgCode():string{
    
    const orgs = ["/iam", "/oes"];
    const router = orgs.find(org => window.location.pathname.includes(org));
    const orgMapping = JSON.parse(localStorage.getItem('allOrgRoutes') || '{}');
    const userOrg = JSON.parse(localStorage.getItem('sales_org') || '[]');
    const orgList = orgMapping[`${router}`];
    if(window.location.href.includes("Salesportal/iam/cart/gettoken")) {
      return 'CN20-G1-40';
    } 
    if (!orgList || orgList.length === 0) {
      return ''; 
    }
    if (orgList.includes('CN20-G1-40') && userOrg.includes('CN20-G1-40')) {
      return 'CN20-G1-40';
    }
  
    const firstMatchingOrg = orgList.find((org:any) => userOrg.includes(org));
  
    return firstMatchingOrg ? firstMatchingOrg : orgList[0];
  }

  isMaterialNumberValid(): boolean {
    let atLeastOneMaterialNumber = false;
    for (const control of this.itemsFormArray.controls) {
      const materialNumberControl = (control as FormGroup).get(
        'materialNumber'
      );
      // Check if the MaterialNumber has a value
      if (materialNumberControl && materialNumberControl.value) {
        //At least one MaterialNumber has a value
        atLeastOneMaterialNumber = true;
        //Check the length of MaterialNumber
        if (materialNumberControl.value.length === 1) {
          //If the length is 1, it means it is invalid
          return true;
        }
      }
    }
    //If at least one MaterialNumber has a value, return false; Otherwise, return true
    return !atLeastOneMaterialNumber;
  }

  get itemsFormArray() {
    return this.form.get('itemsFormArray') as FormArray;
  }

  //Custom validator function
  materialNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value || '';
      if (value.length == 1) {
        return { materialNumberValidator: { desc: this.translate.instant('INVALID_MATERIAL_NUMBER') } };
      }
      //validation is true
      return null;
    };
  }

  async addToCart(): Promise<void> {
    const products: Product[] = [];
    this.itemsFormArray.controls.forEach((control: AbstractControl) => {
      let productNumber = control.get('materialNumber')?.value;
      if (productNumber) {
        const dateValue: Date | null = control.get('date')?.value || new Date();
        const formattedDate: string = dateValue
          ? this.commonService.formatDateString(dateValue)
          : '';
        if(localStorage.getItem('country')=='/iam'){
          productNumber = productNumber.trim().replace(/\s+/g, '').substring(0, 10).toUpperCase();
        }
        const product: Product = {
          inputMaterialNumber: productNumber,
          expectedDate: formattedDate,
          quantity: +control.get('quantity')?.value || 1,
        };

        products.push(product);
      }
    });
    //max length:500
    if (products.length > 500) {
      this.message.create('warning', this.translate.instant('ADD_MAX_NUM_TIPS'));
      return;
    }
    await this.cartService.addToCart(products).then(() => {
      const currentUrl = this.router.url;
      const country = localStorage.getItem("country"); 
      if (currentUrl !== `${country}/cart`) {
        this.router.navigateByUrl(`${country}/cart`);
      } 
      this.closeModal.emit();
    
    });
  }

  minusQuantity(index: number): void {
    const controlAtIndex = this.itemsFormArray.at(index) as FormGroup;
    if (controlAtIndex) {
      const quantityControl = controlAtIndex.get('quantity');
      if (quantityControl) {
        const quantityValue = parseInt(quantityControl.value, 10);
        if (!isNaN(quantityValue) && quantityValue > 1) {
          const newQuantityValue = quantityValue - 1;
          quantityControl.setValue(newQuantityValue.toString());
        }
      }
    }
  }

  addQuantity(index: number): void {
    const controlAtIndex = this.itemsFormArray.at(index) as FormGroup;
    if (controlAtIndex) {
      const quantityControl = controlAtIndex.get('quantity');
      if (quantityControl) {
        const quantityValue = parseInt(quantityControl.value, 10);
        if (!isNaN(quantityValue)) {
          const newQuantityValue = quantityValue + 1;
          quantityControl.setValue(newQuantityValue.toString());
        }
      }
    }
  }

  clearAll() {
    //Traverse each row and reset the data to its initial state
    this.itemsFormArray.controls.forEach((control: AbstractControl) => {
      //Using type assertions to explicitly declare control as a FormGroup type
      (control as FormGroup).reset(this.initialItemData);
    });
  }
  addFormItem() {
    const newItem = this.fb.group({
      materialNumber: [
        this.initialItemData.materialNumber,
        [Validators.required, this.materialNumberValidator()],
      ],
      date: [this.initialItemData.date],
      quantity: [this.initialItemData.quantity],
    }) as FormGroup;
    this.itemsFormArray.push(newItem);
  }
  addLines() {
    //Add 5 rows of initial data
    for (let i = 0; i < 5; i++) {
      this.addFormItem();
    }
  }

  removeFormItem(index: number) {
    const controlAtIndex = this.itemsFormArray.at(index) as FormGroup;
    if (controlAtIndex) {
      controlAtIndex.reset(this.initialItemData);
    }
  }
  goCart() {
    const country = localStorage.getItem("country");
    this.router.navigateByUrl(`${country}/cart`);
  }
}
